"use client";

import Link from "next/link";
import { Facebook, Instagram, Twitter, Linkedin, Mail, Phone, MapPin, Clock } from "lucide-react";
import { useSiteSettings } from "@/lib/site-settings-context";
import Image from "next/image";

export default function Footer() {
  const { settings } = useSiteSettings();
  
  return (
    <footer className="bg-[#192841] text-white">
      <div className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center text-center md:text-left">
          {/* Company Information */}
          <div className="w-full max-w-xs flex flex-col items-center md:items-start">
            <div className="mb-5">
              <Image 
                src="/images/about/logo2.jpg"
                alt="Ocean Tent Global"
                className="h-24 w-24"
                width={240}
                height={80}
              />
            </div>
            <p className="text-gray-300 mb-5 text-center md:text-left">
              {settings.store_tagline}
            </p>
            <div className="flex justify-center md:justify-start space-x-5">
              {settings.social_links.facebook && (
                <a href={settings.social_links.facebook} aria-label="Facebook" className="text-white hover:text-brand-gold transition-colors">
                  <Facebook size={20} />
                </a>
              )}
              {settings.social_links.instagram && (
                <a href={settings.social_links.instagram} aria-label="Instagram" className="text-white hover:text-brand-gold transition-colors">
                  <Instagram size={20} />
                </a>
              )}
              {settings.social_links.twitter && (
                <a href={settings.social_links.twitter} aria-label="Twitter" className="text-white hover:text-brand-gold transition-colors">
                  <Twitter size={20} />
                </a>
              )}
              {settings.social_links.linkedin && (
                <a href={settings.social_links.linkedin} aria-label="LinkedIn" className="text-white hover:text-brand-gold transition-colors">
                  <Linkedin size={20} />
                </a>
              )}
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="w-full max-w-xs">
            <h3 className="text-lg font-semibold mb-5">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/about-us" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link href="/faqs" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  FAQs
                </Link>
              </li>
              <li>
                <Link href="/shipping-delivery" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  Delivery Information
                </Link>
              </li>
              <li>
                <Link href="/refunds-returns" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  Returns Policy
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Customer Services */}
          <div className="w-full max-w-xs">
            <h3 className="text-lg font-semibold mb-5">Customer Services</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/account" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  My Account
                </Link>
              </li>
              <li>
                <Link href="/wishlist" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  My Wishlist
                </Link>
              </li>
              <li>
                <Link href="/account/orders" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  Order Tracking
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link href="/event-party-planner" className="text-gray-300 hover:text-[#9A2643] transition-colors">
                  Events & Party Planning
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Contact Information */}
          <div className="w-full max-w-xs">
            <h3 className="text-lg font-semibold mb-5">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 justify-center md:justify-start">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <span className="text-gray-300">
                  {settings.address}
                </span>
              </li>
              <li className="flex items-center gap-3 justify-center md:justify-start">
                <Phone size={18} />
                <span className="text-gray-300">{settings.contact_phone}</span>
              </li>
              <li className="flex items-center gap-3 justify-center md:justify-start">
                <Mail size={18} />
                <span className="text-gray-300">{settings.contact_email}</span>
              </li>
              <li className="flex items-start gap-3 justify-center md:justify-start">
                <Clock size={18} className="mt-1 flex-shrink-0" />
                <span className="text-gray-300">
                  Mon-Fri: 9am-6pm <br />
                  Sat: 10am-4pm
                </span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Newsletter */}
        <div className="mt-14 border-t border-gray-700 pt-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-1">
              <h3 className="text-lg font-semibold mb-2">Join Our Newsletter</h3>
              <p className="text-gray-300 text-sm">
                Subscribe to get special offers, free giveaways, and once-in-a-lifetime deals.
              </p>
            </div>
            <div className="md:col-span-2">
              <form className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  placeholder="Your email address" 
                  className="px-4 py-2 rounded-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-brand-gold flex-grow"
                  required
                />
                <button 
                  type="submit" 
                  className="px-6 py-2 bg-[#9A2643] text-white font-medium rounded-md hover:bg-[#7A1E35] transition-colors"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>
        
        {/* Payment Methods & Copyright */}
        <div className="mt-10 flex flex-col sm:flex-row justify-between items-center">
          <div className="mb-4 sm:mb-0">
            <p className="text-gray-400 text-sm">
              {settings.footer_content}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="bg-white px-2 py-1 rounded">
              <span className="text-xs font-bold text-gray-800">VISA</span>
            </div>
            <div className="bg-white px-2 py-1 rounded">
              <span className="text-xs font-bold text-gray-800">MASTERCARD</span>
            </div>
            <div className="bg-white px-2 py-1 rounded">
              <span className="text-xs font-bold text-gray-800">AMEX</span>
            </div>
            <div className="bg-white px-2 py-1 rounded">
              <span className="text-xs font-bold text-gray-800">PAYPAL</span>
            </div>
          </div>
        </div>
        
        {/* Legal Links */}
        <div className="mt-8 text-center">
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
            <li>
              <Link href="/cookie-policy" className="hover:text-brand-gold transition-colors">
                Cookie Policy
              </Link>
            </li>
            <li>
              <Link href="/responsible-drinking" className="hover:text-brand-gold transition-colors">
                Responsible Drinking
              </Link>
            </li>
            <li>
              <Link href="/security-privacy" className="hover:text-brand-gold transition-colors">
                Security & Privacy
              </Link>
            </li>
            <li>
              <Link href="/slavery-human-trafficking-policy" className="hover:text-brand-gold transition-colors">
                Slavery & Human Trafficking Policy
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}